import {getRandomString} from "@/tools";
import {useCallback} from "react";
import {
    CreateExtensionInfoQuery,
    useCreateExtensionInfoQuery,
    useCreateExtensionSuperuserQuery,
} from "./queries.generated";
import {UserPackageInfo, DomainType, DeviceTypeSet} from "@/api";
import {useAuth} from "@/context/AuthProvider";
import {CreateExtensionFormData} from "@/components/extension/create/CreateExtensionForm";
import {DeviceTypeSetsInfo} from "../limit-settings/DataSource";
import {useDefaultLanguage} from "@/hooks";

const getDeviceTypeSetsInfo = (dtsQuota: DeviceTypeSetAllocationInfo[]): DeviceTypeSetsInfo => {
    const userAllocated: Record<string, number> = {};
    const domain: Record<string, {used: number; allocated: number}> = {};
    const systemLimit: Record<string, {totalAllocated: number; limit: number}> = {};

    for (const dts of dtsQuota) {
        userAllocated[dts.name] = 0;
        domain[dts.name] = {
            used: dts.domainUsed ?? 0,
            allocated: dts.domainAllocated ?? 0,
        };
        systemLimit[dts.name] = {
            totalAllocated: dts.totalAllocated ?? 0,
            limit: dts.systemLimit,
        };
    }

    return {
        userAllocated,
        domain,
        systemLimit,
    };
};

export type SuperuserInformation = {
    totalCapabilityUsage: {
        [name: string]: number;
    };
    totalDeviceUsage: {
        [name: string]: number;
    };
};

type SuperuserInformationHook = {
    loading: boolean;
    refetch: () => Promise<SuperuserInformation>;
    info: SuperuserInformation;
};

function useSuperuserInfo(domainId: number, isSuperuser: boolean, isClosed: boolean): SuperuserInformationHook {
    // NOTE: it is actually safe to use 'if' here, as isSuperuser is guaranteed to not change
    if (!isSuperuser)
        return {
            info: undefined,
            refetch: async () => undefined,
            loading: false,
        };

    const allocationQuery = useCreateExtensionSuperuserQuery({
        variables: {
            domainId,
        },
        skip: isClosed,
        fetchPolicy: "no-cache",
    });

    const anyLoading = isClosed || allocationQuery.loading;

    const refetch = useCallback(async () => {
        if (anyLoading) return undefined;
        const {data} = await allocationQuery.refetch();

        const info: SuperuserInformation = {
            totalCapabilityUsage: {},
            totalDeviceUsage: {},
        };

        for (const cap of data.domainAllocation.capabilitySets) {
            info.totalCapabilityUsage[cap.name] = cap.totalAllocated;
        }

        for (const dev of data.domainAllocation.deviceTypeSets) {
            info.totalDeviceUsage[dev.name] = dev.totalAllocated;
        }

        return info;
    }, [anyLoading, allocationQuery]);

    if (anyLoading)
        return {
            refetch,
            loading: true,
            info: undefined,
        };

    const info: SuperuserInformation = {
        totalCapabilityUsage: {},
        totalDeviceUsage: {},
    };

    for (const cap of allocationQuery.data.domainAllocation.capabilitySets) {
        info.totalCapabilityUsage[cap.name] = cap.totalAllocated;
    }

    for (const dev of allocationQuery.data.domainAllocation.deviceTypeSets) {
        info.totalDeviceUsage[dev.name] = dev.totalAllocated;
    }

    return {
        loading: false,
        refetch,
        info,
    };
}

type Location = CreateExtensionInfoQuery["locations"]["items"][0];
type CapabilityAllocationInfo = CreateExtensionInfoQuery["domainAllocation"]["capabilitySets"][0];
type DeviceTypeSetAllocationInfo = CapabilityAllocationInfo;
export type UserPackageAllocationInfo = CreateExtensionInfoQuery["domainAllocation"]["userPackages"][0];

export type DataSource = {
    loading: boolean;
    refetch: () => Promise<void>;

    superuserInformation?: SuperuserInformation;
    deviceTypeSets: DeviceTypeSet[];
    userPackageAllocationInfo: UserPackageAllocationInfo[];
    userPackages: UserPackageInfo[];

    domainQuotas: {
        capabilitySets: CapabilityAllocationInfo[];
        deviceTypeSets: DeviceTypeSetAllocationInfo[];
    };

    isResidential: boolean;
    availableTranslations: string[];

    locations: Location[];

    defaultUserPackage: string;
    defaultUserCapabilitySets: string[];
    defaultDeviceLimits: any;
    defaultLanguage: string;

    deviceTypeSetsInfo: DeviceTypeSetsInfo;

    getPackageCapabilitySets(packageName: string): string[];
    getPackageDeviceTypeSets(packageName: string): Record<string, number>;
};

export function useDataSource(domainId: number, isClosed: boolean): DataSource {
    const auth = useAuth();
    const isSuperuser = auth.isSuperuser;

    const createExtensionInfo = useCreateExtensionInfoQuery({
        variables: {
            domainId,
            isSuperuser,
        },
        skip: isClosed,
        fetchPolicy: "no-cache",
    });

    const {
        loading: superuserLoading,
        info: superuserInfo,
        refetch: superuserRefetch,
    } = useSuperuserInfo(domainId, isSuperuser, isClosed);

    const {defaultLanguage, loading: defaultLanguageLoading} = useDefaultLanguage({domainId});

    const anyLoading = isClosed || createExtensionInfo.loading || superuserLoading || defaultLanguageLoading;

    const refetch = useCallback(async () => {
        if (anyLoading) return;
        await Promise.all([createExtensionInfo.refetch(), superuserRefetch()]);
    }, [createExtensionInfo, superuserRefetch, anyLoading]);

    const getPackageCapabilitySets = useCallback(
        (packageName: string) => {
            return createExtensionInfo.data.userPackages.find((p) => p.name === packageName)?.capabilitySets ?? [];
        },
        [createExtensionInfo.data],
    );

    const getPackageDeviceTypeSets = useCallback(
        (packageName: string) => {
            if (packageName === null) return {};
            const packageDtss =
                createExtensionInfo.data.userPackages.find((p) => p.name === packageName)?.deviceTypeSets ?? [];

            const result: Record<string, number> = {};

            // first we set all device type sets to 0, so that we have an entry for all of them (including those not in the package)
            for (const dts of createExtensionInfo.data.deviceTypeSets) {
                result[dts.name] = 0;
            }

            for (const dts of packageDtss) {
                result[dts.name] = dts.quantity;
            }

            return result;
        },
        [createExtensionInfo.data],
    );

    if (anyLoading)
        return {
            loading: true,
            refetch,
            superuserInformation: undefined,
            domainQuotas: undefined,
            deviceTypeSets: undefined,
            userPackageAllocationInfo: undefined,
            userPackages: undefined,
            isResidential: false,
            availableTranslations: undefined,

            locations: undefined,
            defaultUserPackage: undefined,
            defaultUserCapabilitySets: undefined,
            defaultDeviceLimits: undefined,
            defaultLanguage,

            deviceTypeSetsInfo: undefined,

            getPackageCapabilitySets: undefined,
            getPackageDeviceTypeSets: undefined,
        };

    const domain = createExtensionInfo.data.domains.items[0];
    const isResidential = domain.domainType === DomainType.Residential;

    const capabilitySetDomainQuotas = createExtensionInfo.data.domainAllocation.capabilitySets;
    const dtsDomainQuotas = createExtensionInfo.data.domainAllocation.deviceTypeSets;
    const deviceTypeSets = createExtensionInfo.data.deviceTypeSets;

    const userPackageAllocationInfo = createExtensionInfo.data.domainAllocation.userPackages;
    const userPackages = createExtensionInfo.data.userPackages;

    const deviceTypeSetsInfo = getDeviceTypeSetsInfo(dtsDomainQuotas);
    console.log("deviceTypeSetsInfo", deviceTypeSetsInfo);

    return {
        loading: false,
        refetch,
        superuserInformation: superuserInfo,
        domainQuotas: {
            capabilitySets: capabilitySetDomainQuotas,
            deviceTypeSets: dtsDomainQuotas,
        },
        deviceTypeSets,
        userPackageAllocationInfo,
        userPackages,

        isResidential,
        availableTranslations: createExtensionInfo.data.availableTranslations,

        locations: createExtensionInfo.data.locations.items,
        defaultUserPackage: domain.defaultUserPackage,
        defaultUserCapabilitySets: domain.defaultCapabilitySetForExtensions || [],
        defaultDeviceLimits: domain.defaultDeviceLimits,
        defaultLanguage,

        deviceTypeSetsInfo,

        getPackageCapabilitySets,
        getPackageDeviceTypeSets,
    };
}

export const createInitialData = (
    defaultDeviceLimits: any,
    defaultLocationId: number,
    defaultUserPackage: string,
    defaultUserCapabilitySets: string[],
    defaultLanguage: string,
): CreateExtensionFormData => {
    const queryDevices = JSON.parse(defaultDeviceLimits ?? "{}");
    const defaultDevices = queryDevices
        ? Object.keys(queryDevices).map((name) => ({
              name,
              value: queryDevices[name],
          }))
        : [];

    return {
        e164: "",
        shortNumber: "",
        displayName: "",
        pin: getRandomString(4, "0123456789"),
        locationId: defaultLocationId,
        profiles: [],
        userPackageName: defaultUserPackage,
        capabilitySets: defaultUserCapabilitySets,
        deviceTypeSets: defaultDevices ?? [],
        login: {
            language: defaultLanguage,
            email: "",
        },
    };
};
